// src/routes/requests/RequestsPage.tsx
import React, { useState } from 'react';
import { 
  Container,
  Title,
  Paper,
  Table,
  Badge,
  Group,
  Button,
  ActionIcon,
  Text,
  Stack,
  ScrollArea,
} from '@mantine/core';
import { IconPlus, IconEye, IconEdit, IconTrash } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { RequestModal } from './components/RequestModal';
import { Request as RequestType } from '../../types/api';

interface Request {
  id: number;
  title: string;
  description: string;
  requestType: string;
  status: number;
  priority: number;
  createdAt: string;
  createdByUserId?: number;
}

export function RequestsPage() {
  const navigate = useNavigate();
  const [createModalOpened, setCreateModalOpened] = useState(false);
  
  const { data: response, isLoading } = useQuery({
    queryKey: ['requests'],
    queryFn: async () => {
      const token = localStorage.getItem('okta-token-storage');
      const { data } = await axios.get(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/api/Request`, {
        headers: {
          Authorization: `Bearer ${token ? JSON.parse(token).accessToken.accessToken : ''}`
        }
      });
      return data;
    }
  });

  // Gestisci il formato della risposta paginata: { data: { items: [...], totalCount, ... } }
  const requests = response?.data?.items || response?.items || [];

  const getStatusBadge = (status: number) => {
    // Allineato con l'enum RequestStatus dal backend
    const statusMap: { [key: number]: { label: string; color: string } } = {
      1: { label: 'Nuova', color: 'blue' },
      2: { label: 'In Lavorazione', color: 'cyan' },
      3: { label: 'In Attesa di Completamento', color: 'yellow' },
      4: { label: 'Completata', color: 'green' },
      5: { label: 'Annullata', color: 'red' },
      6: { label: 'Sospesa', color: 'gray' },
      7: { label: 'Pre-Campionatura', color: 'grape' },
      8: { label: 'Pre-Campionatura Completata', color: 'indigo' }
    };
    const statusInfo = statusMap[status] || { label: 'Sconosciuto', color: 'gray' };
    return <Badge color={statusInfo.color} style={{ color: 'white' }}>{statusInfo.label}</Badge>;
  };

  const getPriorityBadge = (priority: number) => {
    // Allineato con l'enum RequestPriority dal backend
    const priorityMap: { [key: number]: { label: string; color: string } } = {
      1: { label: 'Bassa', color: 'gray' },
      2: { label: 'Media', color: 'blue' },
      3: { label: 'Alta', color: 'orange' },
      4: { label: 'Urgente', color: 'red' }
    };
    const priorityInfo = priorityMap[priority] || { label: 'Sconosciuto', color: 'gray' };
    return <Badge color={priorityInfo.color} style={{ color: 'white' }}>{priorityInfo.label}</Badge>;
  };

  const handleViewRequest = (id: number) => {
    navigate({ to: `/requests/${id}` });
  };

  const handleEditRequest = (id: number) => {
    navigate({ to: `/requests/${id}/edit` });
  };

  const handleDeleteRequest = (id: number) => {
    if (confirm('Sei sicuro di voler eliminare questa richiesta?')) {
      // TODO: Implementare la chiamata API per eliminare
      console.log('Delete request:', id);
    }
  };

  const handleNewRequest = () => {
    setCreateModalOpened(true);
  };

  const handleRequestCreated = (request: RequestType) => {
    setCreateModalOpened(false);
    // Ricarica la lista
    window.location.reload();
  };

  if (isLoading) {
    return (
      <Container size="xl" py="lg">
        <Text>Caricamento...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Richieste</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleNewRequest}>
            Nuova Richiesta
          </Button>
        </Group>

        <Paper withBorder p="md">
          <ScrollArea type="auto">
          <Table striped highlightOnHover miw={700}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Titolo</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th>Priorità</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Data Creazione</Table.Th>
                <Table.Th>Azioni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {requests?.map((request: Request) => (
                <Table.Tr key={request.id} onClick={() => handleViewRequest(request.id)} style={{ cursor: 'pointer' }}>
                  <Table.Td>{request.id}</Table.Td>
                  <Table.Td>
                    <Text fw={500}>{request.title}</Text>
                    <Text size="sm" c="dimmed">{request.description?.substring(0, 50)}...</Text>
                  </Table.Td>
                  <Table.Td>{request.status}</Table.Td>
                  <Table.Td>{request.priority}</Table.Td>
                  <Table.Td>{request.requestType}</Table.Td>
                  <Table.Td>{new Date(request.createdAt).toLocaleDateString()}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={(e) => { e.stopPropagation(); handleViewRequest(request.id); }}
                        title="Visualizza"
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="yellow"
                        onClick={(e) => { e.stopPropagation(); handleEditRequest(request.id); }}
                        title="Modifica"
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={(e) => { e.stopPropagation(); handleDeleteRequest(request.id); }}
                        title="Elimina"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ScrollArea>
        </Paper>
      </Stack>

      {/* Modale per creare nuova richiesta */}
      <RequestModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        mode="create"
        onSuccess={handleRequestCreated}
      />
    </Container>
  );
}
